import type { CalibrationSessionType, CalibrationStatus } from '../calibrationTypes'
import type { DayId } from '../days'
import { weekdayFromIsoDate, WEEKDAY_IDS } from '../trainingSchedule'

/** Ordered calibration arc — all four must complete before production engine. */
export const CALIBRATION_SESSION_SEQUENCE: CalibrationSessionType[] = [
  'upper_compound',
  'lower_compound',
  'cardio_efficiency',
  'isolation_density',
]

export type CalibrationFeatureCard = 'readiness_check' | 'grocery_matcher'

export type CalibrationSessionStatus = 'scheduled' | 'completed' | 'missed' | 'rolled_over'

export type CalibrationSessionSlot = {
  sessionNumber: 1 | 2 | 3 | 4
  sessionType: CalibrationSessionType
  status: CalibrationSessionStatus
  assignedDate: string
  rolledFromDate?: string
}

export type CalibrationDayEntry = {
  date: string
  dayId: DayId
  isRestDay: boolean
  isMealPrepDay: boolean
  isWorkoutDay: boolean
  session: CalibrationSessionSlot | null
  featureCards: CalibrationFeatureCard[]
  title: string
  subtitle: string
}

export type CalibrationCalendarLayout = {
  phaseStartDate: string
  phaseEndDate: string
  phaseWeeks: 1 | 2
  workoutDayIds: DayId[]
  entries: CalibrationDayEntry[]
  sessions: CalibrationSessionSlot[]
  nextPendingSession: CalibrationSessionSlot | null
  isPhaseComplete: boolean
}

export type CalibrationProfileInput = {
  weekly_commitment: number
  rest_days: string[]
  meal_prep_day: string
  calibration_completed_sessions?: number
  calibration_status?: CalibrationStatus
}

export type CalibrationCalendarPersisted = {
  phaseStartDate: string
  sessions: CalibrationSessionSlot[]
  completedSessionNumbers: number[]
}

export const SESSION_COPY: Record<
  CalibrationSessionType,
  { title: string; subtitle: string }
> = {
  upper_compound: {
    title: 'Upper Compound',
    subtitle: 'Tempo-gated pressing & pulling diagnostics',
  },
  lower_compound: {
    title: 'Lower Compound',
    subtitle: 'Squat/hinge load profiling with velocity tracking',
  },
  cardio_efficiency: {
    title: 'Cardio Efficiency',
    subtitle: 'Threshold pace-to-HR ratio capture',
  },
  isolation_density: {
    title: 'Isolation Density',
    subtitle: 'Strict-rest work density drop-off mapping',
  },
}

const READINESS_COPY = {
  title: 'Nervous System Readiness',
  subtitle: 'Passive sleep/HR sync or subjective soreness log',
}

const GROCERY_COPY = {
  title: 'Meal Prep · Grocery Match',
  subtitle: 'Test recipes against your local SKU clusters',
}

function normalizeDayId(value: string): DayId | null {
  const id = value.toLowerCase() as DayId
  return WEEKDAY_IDS.includes(id) ? id : null
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00`)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function mondayOfWeekContaining(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`)
  const day = d.getDay()
  const mondayOffset = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + mondayOffset)
  return d.toISOString().slice(0, 10)
}

/** Monday of the week containing `isoDate` — anchors slot assignment to full weeks. */
export function calibrationPhaseAnchor(isoDate: string): string {
  return mondayOfWeekContaining(isoDate)
}

/** Non-rest weekdays the user trains on — never altered by calibration overlay. */
export function resolveWorkoutDayIds(restDays: string[]): DayId[] {
  const rest = new Set(restDays.map((d) => d.toLowerCase()))
  return WEEKDAY_IDS.filter((id) => !rest.has(id))
}

/** Chronological training slot dates across `weekCount` Monday-start weeks from anchor. */
export function collectTrainingSlots(
  anchorMonday: string,
  workoutDayIds: DayId[],
  weekCount: 1 | 2,
): { date: string; dayId: DayId }[] {
  const slots: { date: string; dayId: DayId }[] = []

  for (let week = 0; week < weekCount; week++) {
    const weekMonday = addDays(anchorMonday, week * 7)
    for (const dayId of workoutDayIds) {
      const dayIndex = WEEKDAY_IDS.indexOf(dayId)
      const date = addDays(weekMonday, dayIndex)
      slots.push({ date, dayId })
    }
  }

  return slots
}

/**
 * Map session numbers 1–4 onto training-slot indices per weekly commitment rules.
 *   4–5 d/wk → all four in week 1
 *   3 d/wk   → 1–3 week 1, 4 on first slot week 2
 *   2 d/wk   → two per week across two weeks
 */
export function sessionSlotIndices(weeklyCommitment: number, slotsPerWeek: number): number[] {
  const commitment = Math.max(2, Math.min(5, weeklyCommitment))

  if (commitment >= 4) return [0, 1, 2, 3]
  if (commitment === 3) return [0, 1, 2, slotsPerWeek]
  return [0, 1, 2, 3]
}

export function phaseWeekCount(weeklyCommitment: number): 1 | 2 {
  return weeklyCommitment >= 4 ? 1 : 2
}

export function buildInitialSessions(
  profile: CalibrationProfileInput,
  anchorMonday: string,
): CalibrationSessionSlot[] {
  const workoutDayIds = resolveWorkoutDayIds(profile.rest_days)
  const weeks = phaseWeekCount(profile.weekly_commitment)
  const slots = collectTrainingSlots(anchorMonday, workoutDayIds, weeks)
  const indices = sessionSlotIndices(profile.weekly_commitment, workoutDayIds.length)

  return CALIBRATION_SESSION_SEQUENCE.map((sessionType, index) => {
    const slot = slots[indices[index]]
    return {
      sessionNumber: (index + 1) as 1 | 2 | 3 | 4,
      sessionType,
      status: 'scheduled' as const,
      assignedDate: slot?.date ?? anchorMonday,
    }
  })
}

function featureCardsForDay(
  isRestDay: boolean,
  isMealPrepDay: boolean,
): CalibrationFeatureCard[] {
  const cards: CalibrationFeatureCard[] = []
  if (isRestDay) cards.push('readiness_check')
  if (isMealPrepDay) cards.push('grocery_matcher')
  return cards
}

function titleForEntry(session: CalibrationSessionSlot | null, featureCards: CalibrationFeatureCard[]): string {
  if (session) return SESSION_COPY[session.sessionType].title
  if (featureCards.includes('grocery_matcher')) return GROCERY_COPY.title
  if (featureCards.includes('readiness_check')) return READINESS_COPY.title
  return 'Open recovery window'
}

function subtitleForEntry(session: CalibrationSessionSlot | null, featureCards: CalibrationFeatureCard[]): string {
  if (session) return SESSION_COPY[session.sessionType].subtitle
  if (featureCards.includes('grocery_matcher')) return GROCERY_COPY.subtitle
  if (featureCards.includes('readiness_check')) return READINESS_COPY.subtitle
  return 'No calibration session scheduled'
}

function sessionOnDate(sessions: CalibrationSessionSlot[], date: string): CalibrationSessionSlot | null {
  return sessions.find((s) => s.assignedDate === date) ?? null
}

/** Build the full phase calendar overlay (1 or 2 weeks from anchor Monday). */
export function buildCalibrationCalendar(
  profile: CalibrationProfileInput,
  anchorMonday: string,
  sessions: CalibrationSessionSlot[],
): CalibrationCalendarLayout {
  const workoutDayIds = resolveWorkoutDayIds(profile.rest_days)
  const weeks = phaseWeekCount(profile.weekly_commitment)
  const restSet = new Set(profile.rest_days.map((d) => d.toLowerCase()))
  const mealPrepDay = normalizeDayId(profile.meal_prep_day)
  const phaseEndDate = addDays(anchorMonday, weeks * 7 - 1)

  const entries: CalibrationDayEntry[] = []
  for (let offset = 0; offset < weeks * 7; offset++) {
    const date = addDays(anchorMonday, offset)
    const resolvedDayId = weekdayFromIsoDate(date)
    const isRestDay = restSet.has(resolvedDayId)
    const isMealPrepDay = mealPrepDay === resolvedDayId
    const isWorkoutDay = workoutDayIds.includes(resolvedDayId)
    const session = sessionOnDate(sessions, date)
    const featureCards = featureCardsForDay(isRestDay, isMealPrepDay)

    entries.push({
      date,
      dayId: resolvedDayId,
      isRestDay,
      isMealPrepDay,
      isWorkoutDay,
      session,
      featureCards,
      title: titleForEntry(session, featureCards),
      subtitle: subtitleForEntry(session, featureCards),
    })
  }

  const isPhaseComplete = profile.calibration_status === 'completed'

  const nextPendingSession =
    sessions.find((s) => s.status !== 'completed') ?? null

  return {
    phaseStartDate: anchorMonday,
    phaseEndDate,
    phaseWeeks: weeks,
    workoutDayIds,
    entries,
    sessions,
    nextPendingSession,
    isPhaseComplete,
  }
}

/** Collect future workout slots after a given date (for rollover targets). */
export function futureWorkoutSlots(
  afterDate: string,
  anchorMonday: string,
  workoutDayIds: DayId[],
  weekCount: 1 | 2,
  occupiedDates: Set<string>,
): { date: string; dayId: DayId }[] {
  const primary = collectTrainingSlots(anchorMonday, workoutDayIds, weekCount)
  const overflow = collectTrainingSlots(addDays(anchorMonday, weekCount * 7), workoutDayIds, 1)
  return [...primary, ...overflow].filter(
    (slot) => slot.date > afterDate && !occupiedDates.has(slot.date),
  )
}

/**
 * Roll a missed session to the next open training slot without renumbering
 * or skipping ahead in the sequence.
 */
export function rolloverMissedSession(
  profile: CalibrationProfileInput,
  anchorMonday: string,
  sessions: CalibrationSessionSlot[],
  missedDate: string,
): CalibrationSessionSlot[] {
  const missed = sessions.find((s) => s.assignedDate === missedDate && s.status !== 'completed')
  if (!missed) return sessions

  const workoutDayIds = resolveWorkoutDayIds(profile.rest_days)
  const weeks = phaseWeekCount(profile.weekly_commitment)
  const occupied = new Set(
    sessions
      .filter((s) => s.sessionNumber !== missed.sessionNumber)
      .map((s) => s.assignedDate),
  )
  const nextSlot = futureWorkoutSlots(missedDate, anchorMonday, workoutDayIds, weeks, occupied)[0]
  if (!nextSlot) {
    return sessions.map((s) =>
      s.sessionNumber === missed.sessionNumber ? { ...s, status: 'missed' as const } : s,
    )
  }

  return sessions.map((s) => {
    if (s.sessionNumber !== missed.sessionNumber) return s
    return {
      ...s,
      status: 'rolled_over' as const,
      assignedDate: nextSlot.date,
      rolledFromDate: missedDate,
    }
  })
}

export function markSessionCompleted(
  sessions: CalibrationSessionSlot[],
  sessionNumber: 1 | 2 | 3 | 4,
  completedDate: string,
): CalibrationSessionSlot[] {
  return sessions.map((s) =>
    s.sessionNumber === sessionNumber
      ? { ...s, status: 'completed' as const, assignedDate: completedDate }
      : s,
  )
}

export function markFirstNSessionsComplete(
  sessions: CalibrationSessionSlot[],
  completedCount: number,
): CalibrationSessionSlot[] {
  const done = Math.max(0, Math.min(4, completedCount))
  return sessions.map((session) =>
    session.sessionNumber <= done ? { ...session, status: 'completed' as const } : session,
  )
}

/** Intercept a freshly onboarded profile and produce the calibration overlay. */
export function generateCalibrationLayoutFromProfile(
  profile: CalibrationProfileInput,
  onboardingDate = new Date().toISOString().slice(0, 10),
  persisted?: CalibrationCalendarPersisted | null,
): CalibrationCalendarLayout {
  const anchorMonday = calibrationPhaseAnchor(onboardingDate)
  const dbCompleted = profile.calibration_completed_sessions ?? 0
  const baseSessions = buildInitialSessions(profile, anchorMonday)

  let sessions = baseSessions
  if (
    persisted?.phaseStartDate === anchorMonday &&
    persisted.sessions.length === 4 &&
    persisted.completedSessionNumbers.length <= dbCompleted
  ) {
    sessions = applyCompletedSessions(persisted.sessions, persisted.completedSessionNumbers)
  } else if (dbCompleted > 0) {
    sessions = markFirstNSessionsComplete(baseSessions, dbCompleted)
  }

  return buildCalibrationCalendar(profile, anchorMonday, sessions)
}

function applyCompletedSessions(
  sessions: CalibrationSessionSlot[],
  completedNumbers: number[],
): CalibrationSessionSlot[] {
  const done = new Set(completedNumbers)
  return sessions.map((s) =>
    done.has(s.sessionNumber) ? { ...s, status: 'completed' as const } : s,
  )
}
